import React, { useLayoutEffect, useRef } from 'react';
import { EnvelopeSimple, GithubLogo } from '@phosphor-icons/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './ContactSection.css';

gsap.registerPlugin(ScrollTrigger);

const CONTACT_THEME_START = 'top 80%';
const CONTACT_THEME_END = 'bottom 80%';

export const setContactPageTheme = (isActive: boolean) => {
  if (typeof document === 'undefined') {
    return;
  }

  if (isActive) {
    document.body.dataset.contactTheme = 'dark';
    return;
  }

  delete document.body.dataset.contactTheme;
};

type ContactSectionProps = {
  eyebrow?: string;
  title?: string;
  email?: string;
};

export const ContactSection: React.FC<ContactSectionProps> = ({
  eyebrow = 'contact',
  title = "let's stay in touch.",
  email = 'xmia665@aucklanduni.ac.nz',
}) => {
  const sectionRef = useRef<HTMLElement>(null);

  useLayoutEffect(() => {
    if (!sectionRef.current) {
      return undefined;
    }

    const trigger = ScrollTrigger.create({
      trigger: sectionRef.current,
      start: CONTACT_THEME_START,
      end: CONTACT_THEME_END,
      invalidateOnRefresh: true,
      onEnter: () => setContactPageTheme(true),
      onEnterBack: () => setContactPageTheme(true),
      onLeave: () => setContactPageTheme(false),
      onLeaveBack: () => setContactPageTheme(false),
      onRefresh: (instance) => setContactPageTheme(instance.isActive),
    });

    return () => {
      trigger.kill();
      setContactPageTheme(false);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="contact-section"
      aria-labelledby="contact-section-title"
    >
      <div className="contact-section-inner">
        <h2 id="contact-section-title" className="contact-section-title">
          {title}
        </h2>
        <div className="contact-section-details" aria-label="Contact details">
          <span className="contact-section-eyebrow">{eyebrow}</span>
          <div className="contact-section-actions">
            <a
              className="contact-section-link-pill contact-section-email"
              href={`mailto:${email}`}
            >
              <EnvelopeSimple size={17} weight="regular" aria-hidden="true" />
              <span>{email}</span>
            </a>
            <a
              className="contact-section-link-pill contact-section-github"
              href="https://github.com/miachillgood"
              target="_blank"
              rel="noreferrer"
              aria-label="Mia Miao on GitHub"
            >
              <GithubLogo size={17} weight="fill" aria-hidden="true" />
              <span>miachillgood</span>
            </a>
          </div>
          <span className="contact-section-copyright">© Mia Miao 2026</span>
        </div>
      </div>
    </section>
  );
};
